// Dev-only handle for the Playwright smoke test (e2e/board-smoke.js).
//
// Exposing the store lets the test assert on real rule state rather than on
// scraped DOM text, and exposing the renderer's projection lets it click actual
// squares — which is the only way to prove picking works end to end.
//
// Absent from production bundles unless VITE_E2E is set at build time. The
// net smoke test needs it against the *built* server — the point of that test
// is to exercise what Railway actually starts, not the dev proxy — so this is
// an explicit opt-in rather than a weakened guard.
//
// It is a debug handle, not a privilege: the server is authoritative, so a
// hostile client could drive its own store from the console regardless.

import type { GameStore } from "./GameStore";
import type { Move } from "src/engine/rules/move";
import type { Position } from "src/engine/rules/position";
import type { RoomClient } from "src/engine/net/RoomClient";

export interface DebugHandle {
  store: GameStore;
  /**
   * The client's renderer, whatever it is: the engine does not know its shape
   * and must not (PLAN-CLIENT-SPLIT §1). The smokes are JavaScript and reach
   * into it by name; day 9 replaces this with the client's own handle.
   */
  renderer?: object;
  net?: RoomClient;
  /**
   * Enough of the rules to play a *legal* move.
   *
   * A smoke that scripts fixed moves is testing the opponent's variety as much
   * as whatever it meant to test — and `dispatch` returns false for an illegal
   * command and swallows it, so a refused move of the test's own looks exactly
   * like the game having stopped. That cost a real debugging session, twice
   * removed: the seed became real, the opponent stopped repeating itself, and
   * a scripted opening started being illegal about a third of the time.
   */
  rules?: {
    legalMoves(position: Position): Move[];
    moveFrom(move: Move): number;
    moveTo(move: Move): number;
    /**
     * Tunable numbers, so a smoke can assert the truth rather than a literal.
     *
     * Every one of these has already moved once — the miner's rate went 1 -> 2
     * and the knight's recipe 6 -> 5 — and each time the smokes failed on a
     * hardcoded copy rather than on the thing they were written to check. A
     * test that says "the badge shows its cost" should ask what the cost is.
     */
    mintCost(type: number): number;
    minerRate: number;
  };
}

declare global {
  interface Window {
    __gambit?: DebugHandle;
  }
}

/**
 * Whether this **build** carries developer tooling.
 *
 * `DEV` is `npm start`; `VITE_E2E` is the built server the net smoke drives,
 * which has to be the same binary Railway starts.
 *
 * **This is no longer the whole answer for the dev menu**, and the split is
 * deliberate. It used to be one definition on the argument that the handle and
 * the menu must agree — but the menu is now also offered to an *account* whose
 * role may `read` `Dev` (`src/client/ai/ui/useDevTools.ts`), and this is not. A panel is
 * a control somebody uses on their own single-player board; `window.__gambit`
 * is a console handle the smokes drive, so exposing it to every approver on
 * the deployment would widen the surface for nothing the panel does not
 * already do. Compile-time here means the handle is absent from a production
 * bundle rather than merely hidden, which is a stronger property and worth
 * keeping. Read this for "is the handle here"; ask `useDevTools` for "may this
 * person open the tools".
 */
export const DEV_TOOLS: boolean = Boolean(import.meta.env.DEV || import.meta.env.VITE_E2E);

export function exposeForTests(patch: Partial<DebugHandle>): void {
  if (!DEV_TOOLS) return;
  window.__gambit = { ...(window.__gambit as DebugHandle), ...patch };
}
